import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import style from './toast.module.scss';

const SuccessIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
    <path
      d="M8 12.5l2.5 2.5L16 9.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ErrorIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="1.8" />
    <path d="M12 8v5M12 16.25v.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

const CloseIcon = () => (
  <svg viewBox="0 0 24 24" width="14" height="14" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M6 6l12 12M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const ICONS = {
  success: SuccessIcon,
  error: ErrorIcon,
};

function Toast({ message, variant = 'success', onClose, autoDismissMs = 3500 }) {
  const [leaving, setLeaving] = useState(false);

  // Auto-dismiss timer. Trigger the leave animation first, then notify the
  // parent so the message can clear after the slide-out completes.
  useEffect(() => {
    if (!message || autoDismissMs <= 0) return undefined;
    const t = setTimeout(() => setLeaving(true), autoDismissMs);
    return () => clearTimeout(t);
  }, [message, autoDismissMs]);

  // Once the leave animation finishes, hand control back to the parent so
  // the next message (or the same one re-triggered) animates in cleanly.
  useEffect(() => {
    if (!leaving) return undefined;
    const t = setTimeout(() => {
      setLeaving(false);
      onClose?.();
    }, 200);
    return () => clearTimeout(t);
  }, [leaving, onClose]);

  if (!message) return null;

  const Icon = ICONS[variant] || SuccessIcon;

  return (
    <div
      className={`${style.toast} ${style[variant] || ''} ${leaving ? style.leaving : ''}`}
      role={variant === 'error' ? 'alert' : 'status'}
      aria-live={variant === 'error' ? 'assertive' : 'polite'}
    >
      <span className={style.accent} />
      <span className={style.iconWrap}>
        <Icon />
      </span>
      <span className={style.body}>{message}</span>
      <button
        type="button"
        className={style.close}
        onClick={() => setLeaving(true)}
        aria-label="Dismiss"
      >
        <CloseIcon />
      </button>
    </div>
  );
}

Toast.propTypes = {
  message: PropTypes.string,
  variant: PropTypes.oneOf(['success', 'error']),
  onClose: PropTypes.func,
  autoDismissMs: PropTypes.number,
};

export default Toast;
